import clsx from 'clsx';
import React, { ButtonHTMLAttributes, MouseEvent, ReactNode } from 'react';

import { Icon } from '~/components/icon/Icon';
import { SvgIconNames } from '~/design-tokens/iconography/SvgIcons';

import styles from './Chip.module.scss';

export type ChipType = 'input' | 'choice' | 'filter';
export type ChipVariation = 'outline' | 'solid';

export interface ChipProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  label?: ReactNode;
  isActive?: boolean;
  type?: ChipType;
  variation?: ChipVariation;
  leftIcon?: SvgIconNames;
  rightIcon?: SvgIconNames;
  leftIconSize?: number;
  leftIconViewBox?: string;
  rightIconSize?: number;
  assetLogo?: React.ReactNode;
  prefixNode?: React.ReactNode;
  onClick?: (e: MouseEvent) => void;
  onLeftIconClick?: () => void;
  onRightIconClick?: (e: MouseEvent) => void;
  ref?: React.Ref<HTMLButtonElement>;
}

export const Chip = ({
  label,
  isActive = false,
  disabled = false,
  type = 'input',
  variation = 'solid',
  leftIconSize = 16,
  leftIconViewBox,
  rightIconSize = 16,
  leftIcon,
  rightIcon,
  assetLogo,
  prefixNode,
  onLeftIconClick,
  onRightIconClick,
  onClick,
  className,
  ref,
  ...rest
}: ChipProps) => {
  return (
    <button
      ref={ref}
      data-chip
      data-testid="chip"
      aria-selected={isActive}
      disabled={disabled}
      onClick={onClick}
      className={clsx(
        styles.container,
        styles[type],
        styles[variation],
        className,
        { [styles.active]: isActive }
      )}
      {...rest}
    >
      {prefixNode}
      {leftIcon && (
        <span className={styles.iconContainer}>
          <Icon
            data-chip-left-icon
            className={styles.leftIcon}
            size={leftIconSize}
            iconName={leftIcon}
            viewBox={leftIconViewBox}
            onClick={onLeftIconClick}
          />
        </span>
      )}

      {assetLogo}
      {label && <span>{label}</span>}
      {rightIcon && type !== 'filter' && (
        <span className={styles.iconContainer}>
          <Icon
            data-chip-right-icon
            className={styles.rightIcon}
            size={rightIconSize}
            iconName={rightIcon}
            onClick={onRightIconClick}
          />
        </span>
      )}
      {type === 'filter' && (
        <span className={styles.iconContainer}>
          <Icon
            className={styles.closeIcon}
            iconName="Close"
            size={12}
            onClick={onRightIconClick}
          />
        </span>
      )}
    </button>
  );
};
